import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowRight, Phone, Calendar, User, Share2, Facebook, Linkedin, LinkIcon, HelpCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import OptimizedImage from '../../components/OptimizedImage';
import SEO from '../../components/SEO';
import { toast } from 'sonner';

const SLUG = 'debarras-succession-annecy-guide';
const PUBLISH_DATE = '2026-04-11';

const BlogSuccessionAnnecy = () => {
  const faq = [
    {
      question: 'Peut-on débarrasser un logement en succession avant la fin des démarches notariales ?',
      answer: 'Oui, dans la plupart des cas. Le notaire peut délivrer une autorisation d\'accès au logement dès le début de la procédure. En revanche, si des objets de valeur significative sont présents (tableaux, bijoux, antiquités), il est recommandé de faire l\'inventaire avec le notaire avant toute intervention. À Annecy, nous travaillons régulièrement en coordination avec les études notariales pour respecter ces étapes.'
    },
    {
      question: 'Que se passe-t-il si les héritiers ne sont pas d\'accord sur ce qu\'il faut garder ?',
      answer: 'C\'est un cas très fréquent. Notre rôle est de faciliter le processus, pas de trancher. Nous mettons de côté tous les objets litigieux dans un espace dédié du logement, et nous laissons les héritiers décider à leur rythme. L\'évacuation des encombrants non contestés peut commencer en parallèle, ce qui libère de l\'espace et clarifie la situation.'
    },
    {
      question: 'Combien de temps faut-il prévoir pour un débarras succession à Annecy ?',
      answer: 'Pour un appartement T3, comptez une demi-journée à une journée. Pour une maison avec dépendances (cave, grenier, garage) comme on en trouve à Cran-Gevrier ou Annecy-le-Vieux, prévoyez 1 à 3 jours selon le volume. Le tri préalable avec les héritiers est en sus et se planifie à votre rythme — nous ne pressons jamais cette étape.'
    },
    {
      question: 'Faut-il vider le logement avant de le mettre en vente ?',
      answer: 'Oui, un bien vidé et nettoyé se vend mieux et plus vite. Sur le marché annécien, où les transactions se concluent rapidement, un logement vide permet aux acheteurs de se projeter. Les agents immobiliers locaux recommandent systématiquement de vider avant les visites. Nous pouvons intervenir en urgence si la mise en vente est imminente.'
    }
  ];

  const jsonLdArticle = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "Débarras succession à Annecy : le guide complet pour les héritiers",
    "description": "Guide pratique pour les héritiers confrontés au débarras d'un logement après un décès à Annecy. Délais, notaire, tri émotionnel, devenir des objets, étude de cas.",
    "image": `https://debeco.fr/blog/${SLUG}/hero.jpg`,
    "datePublished": PUBLISH_DATE,
    "dateModified": PUBLISH_DATE,
    "author": { "@type": "Organization", "name": "DEBECO" },
    "publisher": {
      "@type": "Organization",
      "name": "DEBECO",
      "logo": { "@type": "ImageObject", "url": "https://debeco.fr/images/logo-header.png" }
    },
    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://debeco.fr/blog/${SLUG}` }
  };

  const jsonLdBreadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://debeco.fr/" },
      { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://debeco.fr/blog" },
      { "@type": "ListItem", "position": 3, "name": "Débarras succession Annecy", "item": `https://debeco.fr/blog/${SLUG}` }
    ]
  };

  const jsonLdFaq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map(q => ({
      "@type": "Question",
      "name": q.question,
      "acceptedAnswer": { "@type": "Answer", "text": q.answer }
    }))
  };

  useEffect(() => {
    const scripts = [jsonLdArticle, jsonLdBreadcrumb, jsonLdFaq].map(data => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-managed', 'true');
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
      return script;
    });
    return () => scripts.forEach(s => s.remove());
  }, []);

  const copyLink = () => {
    navigator.clipboard.writeText(`https://debeco.fr/blog/${SLUG}`);
    toast.success('Lien copié !');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <SEO
        title="Débarras succession Annecy : guide complet 2026 | DEBECO"
        description="Héritiers, ce guide vous accompagne étape par étape : délais légaux, rôle du notaire, tri émotionnel, devenir des objets. Devis gratuit DEBECO."
        canonical={`/blog/${SLUG}`}
        ogImage={`/blog/${SLUG}/hero.jpg`}
      />

      {/* Breadcrumb */}
      <nav className="bg-white border-b" aria-label="Fil d'Ariane" data-testid="breadcrumb">
        <div className="container mx-auto px-4 py-3">
          <ol className="flex items-center text-sm text-gray-600 flex-wrap gap-y-1">
            <li><Link to="/" className="hover:text-emerald-600">Accueil</Link></li>
            <li><ChevronRight className="w-4 h-4 mx-2 text-gray-400" /></li>
            <li><Link to="/blog" className="hover:text-emerald-600">Blog</Link></li>
            <li><ChevronRight className="w-4 h-4 mx-2 text-gray-400" /></li>
            <li className="text-blue-900 font-medium">Débarras succession Annecy</li>
          </ol>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-950 via-blue-900 to-blue-950 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <div className="flex items-center gap-2 mb-4">
              <span className="bg-emerald-500 text-white px-3 py-1 rounded-full text-sm font-medium">Guide succession</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold mb-6" data-testid="article-h1">Débarras succession à Annecy : le guide complet pour les héritiers</h1>
            <div className="flex flex-wrap items-center gap-6 text-blue-200">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <time dateTime={PUBLISH_DATE}>11 avril 2026</time>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-5 h-5" />
                <span>DEBECO</span>
              </div>
              <span className="text-blue-300">~10 min de lecture</span>
            </div>
          </div>
        </div>
      </section>

      {/* Article */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Hero image */}
            <figure className="mb-12 rounded-2xl overflow-hidden shadow-2xl">
              <OptimizedImage
                src={`/blog/${SLUG}/hero.jpg`}
                webpSrc={`/blog/${SLUG}/hero.webp`}
                alt="Débarras succession maison Annecy accompagnement héritiers"
                className="w-full h-96 object-cover"
                width="1536"
                height="1024"
                decoding="async"
                data-testid="article-hero-image"
              />
              <figcaption className="bg-gray-100 px-4 py-2 text-xs text-gray-500 text-center">
                Intérieur d'un logement en succession à Annecy — un accompagnement respectueux fait toute la différence.
              </figcaption>
            </figure>

            <Card className="border-2">
              <CardContent className="p-8 md:p-12">
                <div className="prose prose-lg max-w-none text-gray-700">

                  {/* Introduction */}
                  <p className="text-xl leading-relaxed mb-6">
                    Perdre un proche est une épreuve. Devoir vider son logement en est une autre. Lorsque la succession s'ouvre et que le notaire entre en jeu, les héritiers se retrouvent face à un chantier aussi concret qu'émotionnel : que faire de cette maison pleine de souvenirs, de ces meubles qui ont accompagné toute une vie, de ces objets dont on ne sait plus s'ils ont de la valeur ou non ?
                  </p>
                  <p className="text-lg leading-relaxed mb-6">
                    À Annecy et dans le bassin annécien, ces situations sont quotidiennes. Les maisons familiales de Cran-Gevrier, les appartements anciens de la Vieille Ville, les villas d'Annecy-le-Vieux occupées depuis des décennies — chacune porte en elle des années de vie accumulée. Ce guide a été rédigé pour vous aider à traverser cette étape avec méthode, en vous indiquant les délais, les interlocuteurs, et le moment opportun pour faire appel à un professionnel.
                  </p>

                  {/* H2 - Les délais à connaître */}
                  <h2 className="text-2xl font-bold text-blue-900 mt-12 mb-6">Les délais à connaître après un décès</h2>
                  <p className="mb-6">
                    Contrairement à une idée reçue, il n'existe pas de délai légal strict pour vider un logement en succession. En revanche, plusieurs échéances pratiques et fiscales encadrent le processus.
                  </p>

                  {/* Timeline infographic */}
                  <div className="my-8 rounded-xl shadow-lg overflow-hidden border" data-testid="chronologie-succession">
                    <div className="bg-blue-950 px-6 py-4">
                      <h3 className="text-white font-bold text-lg m-0">Chronologie type après un décès — Repères pour les héritiers</h3>
                    </div>
                    <div className="divide-y">
                      {[
                        { delai: '0 – 7 jours', color: 'bg-red-500', titre: 'Démarches urgentes', desc: 'Déclaration de décès, contact du notaire, premières formalités administratives. Le logement reste en l\'état.' },
                        { delai: '1 – 4 semaines', color: 'bg-amber-500', titre: 'Inventaire et sécurisation', desc: 'Le notaire peut demander un inventaire du mobilier. Sécurisez les objets de valeur (bijoux, documents, œuvres). Aucune évacuation à ce stade.' },
                        { delai: '1 – 3 mois', color: 'bg-blue-500', titre: 'Tri et décisions familiales', desc: 'Répartition des souvenirs entre héritiers, identification des objets à conserver, vendre, donner ou jeter. C\'est le moment de contacter un professionnel du débarras pour une visite.' },
                        { delai: '2 – 6 mois', color: 'bg-emerald-500', titre: 'Débarras et remise en état', desc: 'Intervention du débarrasseur, nettoyage du logement, préparation à la vente ou à la remise aux héritiers. Idéalement avant la déclaration de succession (6 mois).' },
                        { delai: '6 mois', color: 'bg-purple-500', titre: 'Déclaration de succession', desc: 'Date limite pour déposer la déclaration de succession auprès de l\'administration fiscale (12 mois en cas de décès à l\'étranger).' }
                      ].map((e) => (
                        <div key={e.delai} className="flex items-stretch">
                          <div className={`${e.color} w-2 flex-shrink-0`} />
                          <div className="flex-1 px-5 py-4">
                            <div className="flex items-center gap-3 mb-1">
                              <span className="bg-blue-100 text-blue-900 font-semibold text-xs px-2.5 py-1 rounded-lg whitespace-nowrap">{e.delai}</span>
                              <p className="font-bold text-blue-900 text-base m-0">{e.titre}</p>
                            </div>
                            <p className="text-gray-600 text-sm m-0 mt-1">{e.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <p className="mb-6">
                    En pratique, la majorité des débarras succession que nous réalisons à Annecy interviennent entre 2 et 4 mois après le décès. Ce délai laisse le temps aux héritiers de faire leur deuil, de consulter le notaire et de prendre les décisions nécessaires sans précipitation.
                  </p>

                  <div className="bg-amber-50 border-l-4 border-amber-500 p-4 my-6 rounded-r-lg">
                    <p className="text-amber-900 font-medium mb-1">Attention si le bien est loué</p>
                    <p className="text-amber-800 text-sm">
                      Si le défunt était locataire, le bail ne se transmet pas automatiquement aux héritiers (sauf exceptions). Le propriétaire peut demander la restitution du logement dans un délai relativement court. Dans ce cas, le débarras doit être planifié rapidement. Contactez-nous dès que possible pour obtenir un devis sous 24 heures.
                    </p>
                  </div>

                  {/* H2 - Avant l'intervention */}
                  <h2 className="text-2xl font-bold text-blue-900 mt-12 mb-6">Avant l'intervention : notaire, indivision et inventaire</h2>

                  <h3 className="text-xl font-bold text-blue-900 mt-8 mb-4">Le rôle du notaire dans le débarras</h3>
                  <p className="mb-6">
                    Le notaire est votre interlocuteur central. C'est lui qui ouvre la succession, identifie les héritiers, et délivre l'acte de notoriété qui vous autorise à agir sur les biens du défunt. Tant que cet acte n'est pas établi, mieux vaut ne rien jeter — mais vous pouvez commencer le tri. À Annecy, les études notariales du quartier des Balmettes, de la rue Royale ou de la rue Sommeiller connaissent bien ces procédures et peuvent vous orienter vers un débarrasseur de confiance.
                  </p>

                  {/* Encadré notaire */}
                  <div className="my-8 rounded-xl shadow-lg overflow-hidden border-2 border-blue-200" data-testid="encadre-notaire">
                    <div className="bg-blue-100 px-6 py-4">
                      <h3 className="text-blue-900 font-bold text-lg m-0">Les questions à poser au notaire avant un débarras</h3>
                    </div>
                    <div className="p-6 space-y-3">
                      {[
                        'L\'acte de notoriété est-il établi ? Qui est autorisé à accéder au logement ?',
                        'Un inventaire officiel du mobilier est-il nécessaire avant l\'évacuation ?',
                        'Y a-t-il des objets de valeur à faire estimer par un commissaire-priseur ?',
                        'L\'indivision est-elle acceptée par tous les héritiers, ou existe-t-il un désaccord ?',
                        'Le logement est-il assuré pendant la période de vacance ?',
                        'Le bien va-t-il être vendu, loué ou conservé par un héritier ?'
                      ].map((q, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <span className="bg-blue-900 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                          <p className="text-gray-700 text-sm m-0">{q}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-blue-900 mt-8 mb-4">Gérer l'indivision entre héritiers</h3>
                  <p className="mb-6">
                    Quand il y a plusieurs héritiers, rien ne peut être évacué sans l'accord de tous — c'est le principe de l'indivision. En pratique, les désaccords portent rarement sur les encombrants (vieux canapé, électroménager usagé), mais plutôt sur les objets à valeur sentimentale ou marchande : le buffet en noyer de la grand-mère, le service de porcelaine, les photos de famille, l'argenterie. Notre conseil : réglez ces questions en amont, idéalement lors d'une réunion familiale sur place. L'intervention du débarrasseur sera ensuite plus fluide et moins stressante pour tout le monde.
                  </p>

                  <h3 className="text-xl font-bold text-blue-900 mt-8 mb-4">L'inventaire : une étape souvent sous-estimée</h3>
                  <p className="mb-6">
                    Avant de vider, il faut savoir ce qu'on vide. L'inventaire peut être simple (liste manuscrite pièce par pièce) ou formel (inventaire notarié, obligatoire si la succession est complexe ou si un héritier est mineur). Dans les maisons annéciennes occupées depuis plusieurs décennies — pensez aux pavillons de Cran-Gevrier ou aux appartements de La Prairie — l'inventaire révèle souvent des surprises : des papiers importants glissés dans un tiroir de commode, des billets de banque cachés dans un livre, ou une collection de timbres oubliée au fond d'un grenier. Prenez le temps de fouiller méthodiquement avant de confier le reste au débarrasseur.
                  </p>

                  {/* H2 - Le tri émotionnel */}
                  <h2 className="text-2xl font-bold text-blue-900 mt-12 mb-6">Le tri émotionnel : comment s'y prendre sans se laisser submerger</h2>
                  <p className="mb-4">
                    C'est sans doute l'étape la plus difficile. Chaque objet ravive un souvenir. On se retrouve à hésiter pendant des heures devant un verre à moutarde ou un pull tricoté main. Et au bout du compte, on n'a rien trié.
                  </p>
                  <p className="mb-4">
                    Voici une méthode concrète, éprouvée auprès de dizaines de familles annéciennes que nous avons accompagnées :
                  </p>
                  <div className="my-6 space-y-3">
                    {[
                      { step: '1', title: 'Commencer par les pièces neutres', desc: 'Garage, cave, grenier, buanderie. Ces espaces contiennent rarement des objets à forte charge émotionnelle. Vous démarrez dans un contexte plus facile, ce qui donne de l\'élan.' },
                      { step: '2', title: 'Appliquer la règle des 4 caisses', desc: '«\u00A0À garder\u00A0», «\u00A0À donner\u00A0», «\u00A0À vendre\u00A0», «\u00A0À jeter\u00A0». Chaque objet entre dans l\'une de ces catégories. Si vous hésitez, placez-le dans une cinquième caisse «\u00A0En attente\u00A0» et revenez-y plus tard.' },
                      { step: '3', title: 'Limiter les sessions à 2-3 heures', desc: 'Au-delà, la fatigue émotionnelle brouille le jugement. Mieux vaut revenir le lendemain avec l\'esprit clair que de tout faire en une journée et le regretter.' },
                      { step: '4', title: 'Photographier avant de jeter', desc: 'Prenez en photo les objets que vous ne pouvez pas garder mais qui vous touchent. Le souvenir reste, l\'encombrement part.' },
                      { step: '5', title: 'Se faire accompagner', desc: 'Un ami, un cousin éloigné, ou un professionnel du débarras. Un regard extérieur aide à prendre des décisions que l\'émotion rend impossibles seul.' }
                    ].map((s) => (
                      <div key={s.step} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl border">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-900 to-blue-700 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-sm font-bold">{s.step}</span>
                        </div>
                        <div>
                          <p className="font-bold text-blue-900 m-0">{s.title}</p>
                          <p className="text-gray-600 text-sm m-0 mt-1">{s.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 my-6 rounded-r-lg">
                    <p className="text-emerald-900 font-medium mb-1">Le conseil DEBECO</p>
                    <p className="text-emerald-800 text-sm">
                      Lors de nos interventions de <Link to="/debarras-succession-annecy" className="text-emerald-700 hover:text-emerald-800 font-medium underline">débarras succession à Annecy</Link>, nous proposons systématiquement aux héritiers de mettre de côté tout objet douteux dans une pièce dédiée. Rien ne part sans validation. Vous gardez le contrôle du processus, même si vous n'êtes pas présent sur place.
                    </p>
                  </div>

                  {/* H2 - Que deviennent les objets */}
                  <h2 className="text-2xl font-bold text-blue-900 mt-12 mb-6">Que deviennent les objets après le débarras ?</h2>
                  <p className="mb-4">
                    C'est une question que chaque famille pose — et c'est légitime. Quand on confie 50 ans de vie accumulée à un tiers, on veut savoir où tout cela finit. Voici le circuit réel des objets chez DEBECO.
                  </p>

                  <div className="my-8 rounded-xl shadow-lg overflow-hidden border" data-testid="devenir-objets">
                    <div className="bg-blue-950 px-6 py-4">
                      <h3 className="text-white font-bold text-lg m-0">Le devenir des objets — Circuit de traitement DEBECO</h3>
                    </div>
                    <div className="divide-y">
                      {[
                        { pct: '30 – 40 %', color: 'bg-emerald-500', cat: 'Réemploi et don', desc: 'Meubles en bon état, électroménager fonctionnel, vêtements propres, livres, vaisselle. Orientés vers les ressourceries du bassin annécien et les associations (Emmaüs, Secours Populaire).' },
                        { pct: '25 – 35 %', color: 'bg-blue-500', cat: 'Recyclage matière', desc: 'Bois, métaux (cuivre, fonte, laiton), carton, textiles usagés. Acheminés vers les centres de tri et recyclage de Haute-Savoie.' },
                        { pct: '5 – 10 %', color: 'bg-amber-500', cat: 'Revente et brocante', desc: 'Mobilier ancien, objets de collection, outils professionnels. Valorisés via les brocanteurs locaux ou les salles de vente de la région.' },
                        { pct: '20 – 30 %', color: 'bg-gray-400', cat: 'Déchetterie agréée', desc: 'Matelas, mousse, aggloméré irrécupérable, objets cassés. Déposés dans les déchetteries agréées du SILA (Syndicat du Lac d\'Annecy).' }
                      ].map((item) => (
                        <div key={item.cat} className="flex items-stretch">
                          <div className={`${item.color} w-2 flex-shrink-0`} />
                          <div className="flex-1 px-5 py-4 flex items-center justify-between gap-4">
                            <div>
                              <p className="font-bold text-blue-900 text-base m-0">{item.cat}</p>
                              <p className="text-gray-600 text-sm m-0 mt-1">{item.desc}</p>
                            </div>
                            <span className="bg-blue-100 text-blue-900 font-semibold text-sm px-3 py-1.5 rounded-lg whitespace-nowrap flex-shrink-0">{item.pct}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="bg-gray-50 px-6 py-2.5 text-xs text-gray-500 text-center">
                      Proportions moyennes constatées sur nos interventions en Haute-Savoie. Varient selon l'état du mobilier.
                    </div>
                  </div>

                  <p className="mb-6">
                    Au total, plus de 70 % des objets évacués lors d'un débarras succession sont réemployés ou recyclés. C'est un engagement fort de DEBECO : le respect des personnes commence par le respect de ce qu'elles ont possédé. Les meubles de votre proche ne finiront pas en vrac dans une benne.
                  </p>

                  {/* H2 - Étude de cas */}
                  <h2 className="text-2xl font-bold text-blue-900 mt-12 mb-6">Étude de cas : une succession dans un pavillon de Cran-Gevrier</h2>
                  <p className="mb-4 text-sm italic text-gray-500">
                    Cas réel anonymisé, avec l'accord de la famille.
                  </p>
                  <p className="mb-4">
                    <strong>La situation :</strong> M. et Mme R. héritent du pavillon de leur mère, décédée à 87 ans. La maison, construite en 1972 dans le quartier du Jourdil à Cran-Gevrier, n'avait jamais été réaménagée. Quatre pièces principales, une cave, un grenier et un garage double — le tout rempli de 50 ans de vie : mobilier massif des années 70, électroménager hors d'âge, vêtements, livres, vaisselle, outils de jardin, archives administratives remontant aux années 60.
                  </p>
                  <p className="mb-4">
                    <strong>La difficulté :</strong> Les deux héritiers vivent à Lyon et ne pouvaient se rendre à Annecy que le week-end. Le notaire avait fixé un objectif de mise en vente sous 3 mois.
                  </p>
                  <p className="mb-4">
                    <strong>Notre intervention :</strong> Première visite un samedi matin, en présence des héritiers. Nous avons parcouru chaque pièce ensemble. Mme R. a identifié une vingtaine d'objets à conserver (albums photos, vaisselle de mariage, horloge comtoise, quelques meubles). Le reste a été photographié et validé pour évacuation. L'intervention a duré deux jours complets.
                  </p>
                  <p className="mb-4">
                    <strong>Le résultat :</strong> Le buffet en noyer et une armoire savoyarde ont été repris par un brocanteur d'Annecy-le-Vieux. Les vêtements en bon état sont partis chez Emmaüs. Le cuivre, la fonte et les vieux outils ont été valorisés en recyclage. Le coût final pour la famille — déduction faite de la valorisation — s'est élevé à 1 800 euros pour l'ensemble de la prestation (débarras, tri, recyclage, balayage). Le pavillon a été mis en vente la semaine suivante.
                  </p>

                  <div className="bg-red-50 border-l-4 border-red-500 p-4 my-6 rounded-r-lg">
                    <p className="text-red-900 font-medium mb-1">Erreur fréquente</p>
                    <p className="text-red-800 text-sm">
                      Ne louez jamais une benne pour vider un logement en succession sans avoir trié au préalable. Nous avons vu des familles jeter par erreur des documents de propriété, des bijoux glissés dans une poche de manteau, ou des tableaux de valeur noyés dans un tas de cadres. Le tri professionnel avant évacuation est indispensable.
                    </p>
                  </div>

                  {/* H2 - Quand faire intervenir un professionnel */}
                  <h2 className="text-2xl font-bold text-blue-900 mt-12 mb-6">Quand faire intervenir un professionnel du débarras ?</h2>
                  <p className="mb-4">
                    Le bon moment, c'est quand le tri émotionnel est fait — ou au moins bien avancé. Concrètement, cela signifie que les héritiers ont retiré tout ce qu'ils souhaitent conserver, que le notaire a donné son feu vert, et que le reste peut partir.
                  </p>
                  <p className="mb-4">
                    Chez DEBECO, nous intervenons sur l'ensemble du bassin annécien : des immeubles historiques de la Vieille Ville aux résidences de standing du tour du lac (Veyrier-du-Lac, Menthon-Saint-Bernard, Talloires-Montmin), en passant par les pavillons de Seynod, Meythet ou Poisy. Chaque configuration est différente, et c'est pourquoi nous commençons toujours par une visite gratuite sur place.
                  </p>
                  <p className="mb-6">
                    Vous pouvez aussi nous contacter en amont, avant même d'avoir terminé le tri. Nous vous guidons sur la marche à suivre et vous donnons une estimation budgétaire préliminaire. Pour découvrir en détail notre méthode d'intervention, consultez notre page dédiée au <Link to="/debarras-succession-annecy" className="text-emerald-600 hover:text-emerald-700 font-medium">débarras succession à Annecy</Link>.
                  </p>

                  <p className="mb-6">
                    Si la succession concerne une maison individuelle avec de nombreuses dépendances, notre <Link to="/debarras-maison-annecy" className="text-emerald-600 hover:text-emerald-700 font-medium">service de débarras de maison à Annecy</Link> est conçu pour ce type d'intervention. Pour les appartements en copropriété, notre <Link to="/debarras-appartement-annecy" className="text-emerald-600 hover:text-emerald-700 font-medium">service de débarras d'appartement</Link> intègre la coordination avec le syndic et la protection des parties communes.
                  </p>

                </div>

                {/* FAQ */}
                <div className="mt-12 border-t-2 pt-10">
                  <div className="flex items-center gap-3 mb-8">
                    <HelpCircle className="w-7 h-7 text-emerald-600" />
                    <h2 className="text-2xl font-bold text-blue-900">Questions fréquentes — Succession</h2>
                  </div>
                  <div className="space-y-4">
                    {faq.map((item, i) => (
                      <div key={i} className="p-5 bg-gray-50 rounded-xl border">
                        <h3 className="font-bold text-blue-900 mb-2">{item.question}</h3>
                        <p className="text-gray-600 leading-relaxed text-sm">{item.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pour aller plus loin */}
                <div className="mt-12 border-t-2 pt-10">
                  <h2 className="text-2xl font-bold text-blue-900 mb-6">Pour aller plus loin</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Link to="/debarras-succession-annecy" className="group">
                      <div className="p-5 bg-blue-50 rounded-xl border-2 border-transparent hover:border-emerald-500 transition-all h-full">
                        <h3 className="font-bold text-blue-900 mb-2 group-hover:text-emerald-700 transition-colors">Débarras succession à Annecy</h3>
                        <p className="text-gray-600 text-sm">Notre service dédié aux successions : tri, coordination notaire, recyclage responsable.</p>
                      </div>
                    </Link>
                    <Link to="/debarras-maison-annecy" className="group">
                      <div className="p-5 bg-blue-50 rounded-xl border-2 border-transparent hover:border-emerald-500 transition-all h-full">
                        <h3 className="font-bold text-blue-900 mb-2 group-hover:text-emerald-700 transition-colors">Débarras de maison à Annecy</h3>
                        <p className="text-gray-600 text-sm">Vide-maison complet quartier par quartier, de la Vieille Ville au Parmelan.</p>
                      </div>
                    </Link>
                    <Link to="/blog/prix-debarras-maison-annecy" className="group">
                      <div className="p-5 bg-blue-50 rounded-xl border-2 border-transparent hover:border-emerald-500 transition-all h-full">
                        <h3 className="font-bold text-blue-900 mb-2 group-hover:text-emerald-700 transition-colors">Combien coûte un débarras à Annecy ?</h3>
                        <p className="text-gray-600 text-sm">Fourchettes de prix détaillées, facteurs de coût et cas du débarras gratuit.</p>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Share */}
                <div className="mt-10 pt-6 border-t flex items-center gap-4 flex-wrap">
                  <span className="text-gray-500 text-sm font-medium flex items-center gap-2"><Share2 className="w-4 h-4" /> Partager :</span>
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=https://debeco.fr/blog/${SLUG}`} target="_blank" rel="noopener noreferrer" className="p-2 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors" aria-label="Partager sur Facebook">
                    <Facebook className="w-5 h-5 text-blue-700" />
                  </a>
                  <a href={`https://www.linkedin.com/sharing/share-offsite/?url=https://debeco.fr/blog/${SLUG}`} target="_blank" rel="noopener noreferrer" className="p-2 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors" aria-label="Partager sur LinkedIn">
                    <Linkedin className="w-5 h-5 text-blue-700" />
                  </a>
                  <button onClick={copyLink} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 text-sm text-gray-600" aria-label="Copier le lien">
                    <LinkIcon className="w-5 h-5" /> Copier le lien
                  </button>
                </div>

                {/* CTA */}
                <div className="mt-10 p-8 bg-gradient-to-br from-blue-900 to-emerald-800 rounded-2xl text-white text-center">
                  <h3 className="text-2xl font-bold mb-3">Besoin d'un débarras succession à Annecy ?</h3>
                  <p className="text-blue-100 mb-6">Accompagnement respectueux, devis gratuit en 24h, coordination avec votre notaire.</p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/contact">
                      <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100 shadow-xl w-full sm:w-auto" data-testid="cta-devis">
                        Demander un devis
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </Link>
                    <a href="tel:0451401617">
                      <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 w-full sm:w-auto">
                        <Phone className="mr-2 w-5 h-5" />
                        04 51 40 16 17
                      </Button>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogSuccessionAnnecy;
